import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    FormHelperText
} from '@mui/material';
import { Save, X } from 'react-feather';

const CategoryDialog = ({ open, category, problemTypes, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        id: null,
        problem: '',
        problem_type: ''
    });

    const [errors, setErrors] = useState({});

    // Initialize form data when category changes
    useEffect(() => {
        if (category) {
            setFormData({
                id: category.id,
                problem: category.problem || '',
                problem_type: category.problem_type || ''
            });
        } else {
            setFormData({
                id: null,
                problem: '',
                problem_type: problemTypes.length > 0 ? problemTypes[0].id : ''
            });
        }
        setErrors({});
    }, [category, problemTypes]);

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Clear error for the field
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.problem.trim()) {
            newErrors.problem = 'Category name is required';
        }

        if (!formData.problem_type) {
            newErrors.problem_type = 'Problem type is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle save
    const handleSave = () => {
        if (validateForm()) {
            onSave(formData, !category);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{ py: 2, px: 3, bgcolor: '#F9FAFB' }}>
                {category ? 'Edit Category' : 'Add New Category'}
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        label="Category Name *"
                        name="problem"
                        value={formData.problem}
                        onChange={handleInputChange}
                        error={!!errors.problem}
                        helperText={errors.problem || 'Examples: Leaking Faucet, Broken Window, Light Fixture'}
                        autoFocus
                        sx={{ mb: 2 }}
                    />

                    <FormControl fullWidth error={!!errors.problem_type}>
                        <InputLabel id="problem-type-label">Problem Type *</InputLabel>
                        <Select
                            labelId="problem-type-label"
                            name="problem_type"
                            value={formData.problem_type}
                            onChange={handleInputChange}
                            label="Problem Type *"
                        >
                            {problemTypes.map(type => (
                                <MenuItem key={type.id} value={type.id}>
                                    {type.problem_type}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.problem_type && (
                            <FormHelperText>{errors.problem_type}</FormHelperText>
                        )}
                    </FormControl>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#F9FAFB' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    startIcon={<X size={16} />}
                    sx={{ borderRadius: '8px' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    startIcon={<Save size={16} />}
                    sx={{ borderRadius: '8px' }}
                >
                    {category ? 'Update Category' : 'Add Category'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CategoryDialog;